<template>
  <div v-if="profit">
    <div :class="$style.title">
      {{ $t("home.profit") }}
      <InlineSelect
        :options="monthsSelectList"
        :current-option="currentOption"
        @change="onMonthChange"
      />
    </div>
    <div :class="$style.chartWrapper">
      <div
        v-if="!isFirstMonth"
        :class="$style.arrow"
        @click="handleArrowClick(1)"
      />
      <div
        v-else
        :class="$style.arrowPlaceholder"
      />
      <ProfitChart
        :chart-values="[profit.deposit, profit.withdrawal, profit.sumSubagentsProfit]"
        :total="profit.total"
      />
      <div
        v-if="!isLastMonth"
        :class="$style.arrowRight"
        @click="handleArrowClick(-1)"
      />
      <div
        v-else
        :class="$style.arrowPlaceholder"
      />
    </div>
    <ProfitStatistics
      :deposit-all-time="profit.depositAllTime"
      :withdrawal-all-time="profit.withdrawalAllTime"
      :total-all-time="profit.totalAllTime"
      :deposit-fee="profit.depositFee"
      :withdrawal-fee="profit.withdrawalFee"
      :current-profit="profit.currentProfit"
      :cashbox-profit="profit.cashboxProfit"
      :master-profit="profit.masterProfit"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { getProfit } from '@/api/profit';
import InlineSelect from '@/components/InlineSelect.vue';
import ProfitChart from '@/components/ProfitChart.vue';
import ProfitStatistics from '@/components/ProfitStatistics.vue';
import MetricTracker from '@/services/MetricTrackers/MetricTracker';

const { locale } = useI18n();

const profit = ref(null);

const currentDate = new Date();
const year = currentDate.getFullYear();
const month = currentDate.getMonth();

const months = computed(() => Array.from({ length: 12 }).map((_, index) => {
  const date = new Date(year, month - index);

  return {
    date,
    monthLocaledName: date.toLocaleString(locale.value, {
      month: 'long',
    }),
  };
}));

const currentMonthIndex = ref(0);

const fromDate = computed(() => months.value[currentMonthIndex.value].date);
const toDate = computed(
  () => new Date(fromDate.value.getFullYear(), fromDate.value.getMonth() + 1),
);

const monthsSelectList = computed(() => months.value.map((item, index) => ({
  text: item.monthLocaledName,
  value: index,
})));

const currentOption = computed(() => ({
  ...monthsSelectList.value[currentMonthIndex.value],
}));

const converRelativeIndexMonthToNumber = (index) => {
  const { date } = months.value[index];
  return date.getMonth() + 1;
};

const onMonthChange = ({ value }) => {
  MetricTracker.setHomeMounth({
    numberMonth: converRelativeIndexMonthToNumber(value),
  });

  currentMonthIndex.value = Number(value);
};

const handleArrowClick = (monthIncrement) => {
  const newMonthIndex = currentMonthIndex.value + monthIncrement;
  if (newMonthIndex < 0 || newMonthIndex > 11) {
    return;
  }

  MetricTracker.setHomeMounthPagination({
    direction: monthIncrement > 0 ? 'left' : 'right',
    numberMonth: converRelativeIndexMonthToNumber(newMonthIndex),
  });

  currentMonthIndex.value = newMonthIndex;
};

const isLastMonth = computed(() => currentMonthIndex.value <= 0);
const isFirstMonth = computed(() => currentMonthIndex.value >= 11);

let latestFetchPromise;

const fetchProfit = async () => {
  const fetchPromise = getProfit(fromDate.value, toDate.value);
  latestFetchPromise = fetchPromise;

  try {
    const {
      data: {
        deposit = 0,
        withdrawal = 0,
        sumSubagentsProfit = 0,
        cashboxProfit,
        masterProfit,
        total,
        depositFee,
        withdrawalFee,
        currentProfit,
        profitAllTime: {
          deposit: depositAllTime,
          withdrawal: withdrawalAllTime,
          total: totalAllTime,
        },
      },
    } = await fetchPromise;

    if (fetchPromise !== latestFetchPromise) {
      return;
    }

    profit.value = {
      deposit,
      withdrawal,
      sumSubagentsProfit,
      total,
      depositFee,
      withdrawalFee,
      currentProfit,
      depositAllTime,
      withdrawalAllTime,
      totalAllTime,
      cashboxProfit,
      masterProfit,
    };
  } catch (err) {
    console.error(err);
  }
};

watch(currentMonthIndex, fetchProfit, { immediate: true });
</script>

<style lang="scss" module>
@import "~@/styles/variables.scss";

.chartWrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 328px;
  margin: 0 auto 22px;
}

.arrowPlaceholder {
  width: 30px;
  height: 30px;
}

.arrow {
  composes: arrowPlaceholder;
  position: relative;
  cursor: pointer;
  overflow: hidden;

  &:after {
    content: "";
    display: block;
    width: 11px;
    height: 11px;
    border-top: 2px solid rgba(92, 114, 163, 0.5);
    border-left: 2px solid rgba(92, 114, 163, 0.5);
    transform: rotate(-45deg);
    position: absolute;
    top: 0;
    bottom: 0;
    left: 12px;
    margin: auto;
  }

  @media (max-width: 375px) {
    transform: translateX(-15px);
  }
}

.arrowRight {
  composes: arrow;
  right: 0;
  left: auto;

  &:after {
    left: auto;
    right: 12px;
    transform: rotate(135deg);
  }

  @media (max-width: 375px) {
    transform: translateX(15px);
  }
}

.title {
  font-family: $font-sf-pro-display;
  font-size: 18px;
  color: $color-black;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
}
</style>
